import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../lib/seo';
import { fetchCmsList } from '../lib/cms';
import { ReviewSlider } from '../components/ReviewSlider';
import { ContactForm } from '../components/ContactForm';

export function Home({ settings }) {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    async function loadData() {
      const [svc, rev, art] = await Promise.all([
        fetchCmsList('services'),
        fetchCmsList('reviews'),
        fetchCmsList('articles')
      ]);
      if (svc.length) setServices(svc);
      if (rev.length) setReviews(rev);
      if (art.length) setArticles(art.slice(0, 3));
    }
    loadData();
  }, []);

  const phone = settings?.phone || "82 82 10 10";
  const hours = settings?.hours || "Mandag til lørdag: 9 - 18";

  return (
    <>
      <SEO 
        title="Fodtid | Professionel mobil fodbehandling direkte i dit eget hjem"
        description="Fodtid tilbyder 100% udkørende professionel fodpleje og fodterapi i dit eget hjem. Vi kører ud på hele Sjælland – minus øerne mandag-lørdag kl. 9-18. Ring 82 82 10 10."
      />

      {/* HERO SECTION - BRIGHT, FRESH & SCENIC WITH SUNLIT CAR & OVERLAID TREATMENT BUBBLES */}
      <section className="relative min-h-[640px] lg:min-h-[720px] flex items-center overflow-hidden bg-[#faf8f5] border-b border-stone-200/80">
        {/* The Big Scenic Hero Image: Service Car in Danish countryside at golden hour */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/samssa-bil-logo-2.png"
            alt="Fodtid mobil klinik – professionel fodbehandling i dit eget hjem"
            className="w-full h-full object-cover object-[72%_center] lg:object-center select-none"
          />
          {/* Luminous light gradient wash: Crisp bright warm-white/cream on the left for text legibility, preserving the warm sunny Danish landscape & car on the right */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#faf8f5] via-[#faf8f5]/94 via-45% to-[#faf8f5]/35 lg:from-[#faf8f5] lg:via-[#faf8f5]/88 lg:via-42% lg:to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#faf8f5]/70 via-transparent to-[#faf8f5]/40" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 items-center">
            
            {/* Content Left: Crisp, bright, elegant typography */}
            <div className="lg:col-span-6 xl:col-span-6 space-y-5 sm:space-y-6 text-left">
              
              {/* Badge: 100% Mobil Klinik */}
              <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-[#F7EEDB] border border-[#A67C3D]/30 text-[#66461D] text-xs font-bold tracking-wide shadow-xs">
                <span className="w-2 h-2 rounded-full bg-[#A67C3D] animate-pulse" />
                <span className="uppercase tracking-wider text-[11px] font-bold">100% Mobil Klinik – Vi kører ud til dig</span>
              </div>

              {/* Main Headings */}
              <div className="space-y-1.5">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[54px] font-extrabold text-stone-900 tracking-tight leading-[1.12]">
                  Professionel fodpleje
                </h1>
                <p className="font-accent text-3xl sm:text-4xl md:text-5xl text-[#A67C3D] font-normal leading-tight">
                  – direkte hjemme hos dig
                </p>
              </div>

              {/* Descriptive Body Copy */}
              <p className="text-sm sm:text-base font-normal leading-relaxed text-stone-700 max-w-lg">
                Vi pakker vores servicebil og bringer professionel, skånsom fodbehandling direkte hjem til din stue. 
                Slip for transport, venteværelser og trapper – nyd behandlingen i din egen yndlingsstol.
              </p>

              {/* Bullet Points with Warm Brand Checkmark Pills */}
              <ul className="space-y-2.5 text-xs sm:text-sm text-stone-800 pt-1">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F7EEDB] text-[#A67C3D] flex items-center justify-center shrink-0 border border-[#A67C3D]/25 shadow-xs">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                  </div>
                  <span><strong>Vi kører ud til dig</strong> – altid inklusiv kørsel og faste trygge priser</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#F7EEDB] text-[#A67C3D] flex items-center justify-center shrink-0 border border-[#A67C3D]/25 shadow-xs">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                  </div>
                  <span><strong>Komplet behandling</strong> med fodbad, neglepleje, hård hud & massage</span>
                </li>
              </ul>

              {/* CTA Buttons */}
              <div className="flex flex-wrap items-center gap-4 pt-3">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-[#A67C3D] hover:bg-[#91692E] text-white font-bold text-xs sm:text-sm tracking-wider uppercase shadow-lg shadow-[#A67C3D]/25 hover:shadow-xl hover:shadow-[#A67C3D]/35 transition-all duration-150 active:scale-95"
                >
                  <i className="fa-solid fa-calendar-check text-white text-sm" aria-hidden="true" />
                  <span>Book en tid</span>
                </Link>

                <Link
                  to="/behandlinger"
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-[#7D7871] hover:bg-[#68635D] text-white font-bold text-xs sm:text-sm tracking-wider uppercase shadow-sm transition-all duration-150 active:scale-95"
                >
                  <span>Se behandlinger</span>
                </Link>
              </div>

              {/* Trust Highlights Strip */}
              <div className="pt-5 flex flex-wrap items-center gap-6 text-xs text-stone-600 border-t border-stone-200/80">
                <span className="flex items-center gap-1.5 font-semibold text-stone-700">
                  <i className="fa-solid fa-location-dot text-[#A67C3D]" aria-hidden="true" />
                  Hele Sjælland – minus øerne
                </span>
                <span className="flex items-center gap-1.5 font-semibold text-stone-700">
                  <i className="fa-solid fa-shield-heart text-[#A67C3D]" aria-hidden="true" />
                  8.000+ behandlinger udført
                </span>
                <span className="flex items-center gap-1.5 font-semibold text-stone-700">
                  <i className="fa-solid fa-phone text-[#A67C3D]" aria-hidden="true" />
                  Ring direkte: <a href={`tel:${phone.replace(/\s+/g, '')}`} className="font-bold text-[#A67C3D] hover:underline transition-colors">{phone}</a>
                </span>
              </div>

            </div>

            {/* Right: Treatment Bubbles Overlaid Brightly & Harmoniously */}
            <div className="lg:col-span-6 xl:col-span-6 flex flex-col items-center justify-center">
              
              {/* Clean, harmonious organic cluster of treatment bubbles - enlarged */}
              <div className="relative w-full max-w-[540px] sm:max-w-[620px] lg:max-w-[640px] h-[440px] sm:h-[500px] lg:h-[530px]">
                
                {/* Luminous soft daylight halo behind bubbles */}
                <div className="absolute inset-0 m-auto w-80 h-80 sm:w-104 sm:h-104 rounded-full bg-white/50 blur-3xl pointer-events-none" />

                {/* BUBBLE 1: Komplet Fodbehandling (Stor dominerende hovedboble) */}
                <div
                  className="absolute top-1/2 left-1/2 -translate-x-[64%] -translate-y-[48%] w-64 h-64 sm:w-76 sm:h-76 md:w-84 md:h-84 lg:w-92 lg:h-92 rounded-full border-[6px] sm:border-[8px] border-white ring-1 ring-stone-900/10 overflow-hidden shadow-2xl shadow-stone-900/25 z-30 bg-stone-100"
                >
                  <img
                    src="/cdn-cgi/image/width=750,quality=85,format=auto/_assets/forside-fodpleje-behandling.webp"
                    alt="Professionel fodpleje behandling"
                    className="w-full h-full object-cover select-none"
                  />
                </div>

                {/* BUBBLE 2: Seniorer / Hjemmebehandling (Øverst til højre) */}
                <div
                  className="absolute top-0 right-1 sm:right-2 md:right-4 w-44 h-44 sm:w-52 sm:h-52 md:w-58 md:h-58 rounded-full border-[5px] sm:border-[7px] border-white ring-1 ring-stone-900/10 overflow-hidden shadow-xl shadow-stone-900/20 z-20 bg-stone-100"
                >
                  <img
                    src="/cdn-cgi/image/width=600,quality=85,format=auto/_assets/forside-seniorer.webp"
                    alt="Seniorer der nyder hverdagen med sunde fødder"
                    className="w-full h-full object-cover select-none"
                  />
                </div>

                {/* BUBBLE 3: Fødder på hvid baggrund (Øverst til venstre) */}
                <div
                  className="absolute top-2 left-0 sm:left-2 w-38 h-38 sm:w-46 sm:h-46 md:w-50 md:h-50 rounded-full border-4 sm:border-[6px] border-white ring-1 ring-stone-900/10 overflow-hidden shadow-lg shadow-stone-900/15 z-10 bg-stone-100"
                >
                  <img
                    src="/cdn-cgi/image/width=500,quality=85,format=auto/_assets/forside-foedder-hvid-baggrund.webp"
                    alt="Sunde og velplejede fødder"
                    className="w-full h-full object-cover select-none"
                  />
                </div>

                {/* BUBBLE 4: Fødder familie (Nederst til højre) */}
                <div
                  className="absolute bottom-1 right-2 sm:right-5 md:right-6 w-36 h-36 sm:w-44 sm:h-44 md:w-48 md:h-48 rounded-full border-4 sm:border-[6px] border-white ring-1 ring-stone-900/10 overflow-hidden shadow-xl shadow-stone-900/20 z-20 bg-stone-100"
                >
                  <img
                    src="/cdn-cgi/image/width=480,quality=85,format=auto/_assets/forside-foedder-familie.webp"
                    alt="Fodpleje til hele familien"
                    className="w-full h-full object-cover select-none"
                  />
                </div>

              </div>

            </div>

          </div>
        </div>
      </section>

      {/* VALUE PROPOSITION BAR - WARM MINIMALIST */}
      <section className="bg-[#FAF7F2] py-12 border-b border-[#ECE6DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-[#ECE6DC]">
            
            {/* Feature 1 */}
            <div className="flex flex-col items-center text-center p-6 space-y-3">
              <div className="text-[#B88E56] mb-1 flex items-center justify-center h-14">
                <i className="fa-light fa-house-heart text-4xl sm:text-5xl" aria-hidden="true" />
              </div>
              <h3 className="text-base font-bold text-stone-900">Jeg kommer til dig</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed max-w-[220px]">
                Du slipper for transport – jeg kommer med alt, der skal bruges.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="flex flex-col items-center text-center p-6 space-y-3">
              <div className="text-[#B88E56] mb-1 flex items-center justify-center h-14">
                <i className="fa-light fa-clock text-4xl sm:text-5xl" aria-hidden="true" />
              </div>
              <h3 className="text-base font-bold text-stone-900">Fleksible tider</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed max-w-[220px]">
                Jeg tilpasser mig din hverdag, så du får en tid, der passer dig.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="flex flex-col items-center text-center p-6 space-y-3">
              <div className="text-[#B88E56] mb-1 flex items-center justify-center h-14">
                <i className="fa-light fa-heart text-4xl sm:text-5xl" aria-hidden="true" />
              </div>
              <h3 className="text-base font-bold text-stone-900">Omsorg &amp; kvalitet</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed max-w-[220px]">
                Din velvære er i centrum. Professionel behandling med hjertet.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="flex flex-col items-center text-center p-6 space-y-3">
              <div className="text-[#B88E56] mb-1 flex items-center justify-center h-14">
                <i className="fa-light fa-leaf text-4xl sm:text-5xl" aria-hidden="true" />
              </div>
              <h3 className="text-base font-bold text-stone-900">Sunde fødder</h3>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed max-w-[220px]">
                Regelmæssig fodpleje forebygger gener og giver dig lettere og gladere fødder.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* STORYTELLING JOURNEY: "FRA SERVICEBILEN TIL DIN STUE" */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#E7E0D2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
            <span className="text-xs uppercase font-bold tracking-widest text-[#66461D] bg-[#F7EEDB] px-4 py-1 rounded-full border border-[#A67C3D]/30 inline-block">
              Enkel og tryg proces
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Sådan foregår et besøg fra Fodtid
            </h2>
            <p className="font-accent text-2xl text-[#A67C3D] -mt-1">
              fra vores servicebil ruller afsted, til du har lette og sunde fødder
            </p>
            <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
              Billederne herunder viser den virkelige proces. Du skal ikke tænke på forberedelse eller transport – vi tager os af det hele.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Step 1 */}
            <div className="bg-[#FAF7F2] rounded-3xl overflow-hidden border border-[#E7E0D2] shadow-xs flex flex-col justify-between group hover:shadow-md transition-all">
              <div>
                <div className="aspect-4/3 overflow-hidden relative">
                  <img 
                    src="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/samssa-bil-logo-2.png" 
                    alt="Fodtid mobil klinik servicebil i det danske gyldne landskab på vej til hjemmebehandling" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-[#A67C3D] text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center shadow">
                    1
                  </div>
                </div>
                <div className="p-6 space-y-2">
                  <h3 className="text-base font-bold text-stone-900">
                    Bilen pakkes & vi kører ud
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Vi pakker vores mobile bil med sterilt podiatrisk udstyr, fodbad, behagelige håndklæder og naturlige urtecremer.
                  </p>
                </div>
              </div>
              <div className="px-6 pb-5 pt-0">
                <span className="text-[11px] font-bold text-[#A67C3D] uppercase tracking-wider flex items-center gap-1.5">
                  <i className="fa-solid fa-van-shuttle" aria-hidden="true" />
                  Præcis ankomsttid
                </span>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-[#FAF7F2] rounded-3xl overflow-hidden border border-[#E7E0D2] shadow-xs flex flex-col justify-between group hover:shadow-md transition-all">
              <div>
                <div className="aspect-4/3 overflow-hidden relative">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/d4428667-cde9-4c31-9224-88278506a01a/fodtid-warm-greeting-doorstep.png" 
                    alt="Venlig ankomst og modtagelse ved hoveddøren" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-[#A67C3D] text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center shadow">
                    2
                  </div>
                </div>
                <div className="p-6 space-y-2">
                  <h3 className="text-base font-bold text-stone-900">
                    Venlig ankomst ved din dør
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Vi parkerer ved din bopæl og banker på med et smil. Du skal blot åbne døren og vise os hen til din foretrukne siddeplads.
                  </p>
                </div>
              </div>
              <div className="px-6 pb-5 pt-0">
                <span className="text-[11px] font-bold text-[#A67C3D] uppercase tracking-wider flex items-center gap-1.5">
                  <i className="fa-solid fa-door-open" aria-hidden="true" />
                  Ingen trappebesvær for dig
                </span>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-[#FAF7F2] rounded-3xl overflow-hidden border border-[#E7E0D2] shadow-xs flex flex-col justify-between group hover:shadow-md transition-all">
              <div>
                <div className="aspect-4/3 overflow-hidden relative">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/d4428667-cde9-4c31-9224-88278506a01a/fodtid-cozy-home-treatment-livingroom.png" 
                    alt="Behagelig behandling i egen stuestol" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-[#A67C3D] text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center shadow">
                    3
                  </div>
                </div>
                <div className="p-6 space-y-2">
                  <h3 className="text-base font-bold text-stone-900">
                    Behandling i yndlingsstolen
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Læn dig tilbage i stuen med en kop kaffe. Vi udfører grundig neglepleje, fjerner hård hud og ligtorne i fuld ro og tryghed.
                  </p>
                </div>
              </div>
              <div className="px-6 pb-5 pt-0">
                <span className="text-[11px] font-bold text-[#A67C3D] uppercase tracking-wider flex items-center gap-1.5">
                  <i className="fa-solid fa-couch" aria-hidden="true" />
                  100% trygge hjemmerammer
                </span>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-[#FAF7F2] rounded-3xl overflow-hidden border border-[#E7E0D2] shadow-xs flex flex-col justify-between group hover:shadow-md transition-all">
              <div>
                <div className="aspect-4/3 overflow-hidden relative">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/d4428667-cde9-4c31-9224-88278506a01a/shutterstock-2670356825.jpg" 
                    alt="Afsluttende fodmassage og naturlig urtecreme" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-[#A67C3D] text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center shadow">
                    4
                  </div>
                </div>
                <div className="p-6 space-y-2">
                  <h3 className="text-base font-bold text-stone-900">
                    Velvære & lette fødder
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Besøget afrundes med en let, beroligende massage med nærende urtecreme, så dine fødder efterlades bløde, velplejede og fri for smerter.
                  </p>
                </div>
              </div>
              <div className="px-6 pb-5 pt-0">
                <span className="text-[11px] font-bold text-[#A67C3D] uppercase tracking-wider flex items-center gap-1.5">
                  <i className="fa-solid fa-heart-pulse" aria-hidden="true" />
                  Umiddelbar lindring & frihed
                </span>
              </div>
            </div>

          </div>

          <div className="mt-12 text-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 bg-[#A67C3D] hover:bg-[#91692E] text-white font-bold px-8 py-3.5 rounded-full text-xs uppercase tracking-wider shadow-md transition-all active:scale-95"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book et hjemmebesøg nu</span>
            </Link>
          </div>

        </div>
      </section>

      {/* CORE BENEFITS SECTION */}
      <section className="py-16 sm:py-24 bg-[#FAF7F2] border-b border-[#E7E0D2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
            <span className="text-xs uppercase font-bold tracking-widest text-[#66461D] bg-[#F7EEDB] px-3.5 py-1 rounded-full border border-[#A67C3D]/30 inline-block">
              Tryghed • Faglighed • Velvære
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Hvorfor vælge Fodtid til dine fødder?
            </h2>
            <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
              Uanset om du har prøvet andre behandlere før, eller om det er første gang du overvejer en fodbehandling, er det afgørende, at du føler dig i rolige og kompetente hænder.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Feature 1 */}
            <div className="bg-white rounded-3xl p-8 border border-[#E7E0D2] space-y-4 shadow-xs">
              <div className="w-14 h-14 rounded-full bg-[#FAF7F2] border border-[#A67C3D]/30 text-[#A67C3D] flex items-center justify-center text-xl">
                <i className="fa-solid fa-house-user" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-stone-900">
                Behandling i eget hjem
              </h3>
              <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                Slip for besværlig transport, regnvejr, trapper og venteværelser. Vi medbringer alt professionelt udstyr, maskiner, lys og fodbad til din stue.
              </p>
              <div className="pt-2">
                <span className="text-xs font-semibold text-stone-900 flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#A67C3D]" aria-hidden="true" />
                  Fleksibelt ankomstvindue ±30 min
                </span>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-3xl p-8 border border-[#E7E0D2] space-y-4 shadow-xs">
              <div className="w-14 h-14 rounded-full bg-[#FAF7F2] border border-[#A67C3D]/30 text-[#A67C3D] flex items-center justify-center text-xl">
                <i className="fa-solid fa-user-doctor" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-stone-900">
                Over 15 års erfaring
              </h3>
              <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                Klaus Just har behandlet over 8.000 fødder siden 2009. Du møder stor tålmodighed, forståelse og erfaring med alle typer fodproblemer og ældrepleje.
              </p>
              <div className="pt-2">
                <span className="text-xs font-semibold text-stone-900 flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#A67C3D]" aria-hidden="true" />
                  Ingen fordømmelse – kun omsorg
                </span>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-3xl p-8 border border-[#E7E0D2] space-y-4 shadow-xs">
              <div className="w-14 h-14 rounded-full bg-[#FAF7F2] border border-[#A67C3D]/30 text-[#A67C3D] flex items-center justify-center text-xl">
                <i className="fa-solid fa-leaf" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-stone-900">
                100% Naturlige produkter
              </h3>
              <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                Vi anvender udelukkende specialudviklede urtecremer og naturlige olier uden skadelig kemi og parabener, tilpasset netop din huds behov.
              </p>
              <div className="pt-2">
                <span className="text-xs font-semibold text-stone-900 flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#A67C3D]" aria-hidden="true" />
                  Effektiv mod tør hud & revner
                </span>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* SERVICES SHOWCASE SECTION */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#E7E0D2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="space-y-1.5">
              <span className="text-xs uppercase font-bold tracking-widest text-[#A67C3D]">
                Vores Ydelser i Hjemmet
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
                Populære Hjemmebehandlinger
              </h2>
            </div>
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 text-xs uppercase font-bold tracking-wider text-stone-800 hover:text-[#A67C3D] transition-colors"
            >
              <span>Se alle behandlingsformer</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Service 1: Komplet Behandling */}
            <div className="bg-[#FAF7F2] rounded-3xl overflow-hidden border border-[#E7E0D2] shadow-xs hover:border-[#A67C3D] transition-all flex flex-col justify-between group">
              <div>
                <div className="aspect-16/10 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/d4428667-cde9-4c31-9224-88278506a01a/fodtid-cozy-home-treatment-livingroom.png"
                    alt="Komplet Fodbehandling i hjemmet"
                    className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3 bg-stone-900 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow">
                    499 kr.
                  </div>
                </div>
                <div className="p-6 space-y-2">
                  <h3 className="text-base font-bold text-stone-900 group-hover:text-[#A67C3D] transition-colors">
                    Komplet Fodbehandling
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Fodbad, undersøgelse, klipning, slibning og polering af negle, fjernelse af hård hud, ligtorne og let massage.
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/ydelser/komplet-fodbehandling"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-stone-50 text-stone-900 font-bold py-2.5 rounded-full text-xs border border-[#E7E0D2] transition-colors"
                >
                  <span>Læs mere om behandlingen</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Service 2: Abonnement */}
            <div className="bg-[#FAF7F2] rounded-3xl overflow-hidden border-2 border-[#A67C3D] shadow-sm hover:shadow-md transition-all flex flex-col justify-between group relative">
              <div className="absolute top-0 right-0 bg-[#A67C3D] text-white text-[10px] font-bold uppercase tracking-wider px-3.5 py-1 rounded-bl-xl z-10">
                Mest populær
              </div>
              <div>
                <div className="aspect-16/10 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/samssa-bil-logo-2.png"
                    alt="Fodpleje Abonnement med fast udkørende servicebil"
                    className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-300"
                  />
                  <div className="absolute top-3 left-3 bg-stone-900 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow">
                    119 kr. / mdr.
                  </div>
                </div>
                <div className="p-6 space-y-2">
                  <h3 className="text-base font-bold text-stone-900 group-hover:text-[#A67C3D] transition-colors">
                    Fodpleje Abonnement
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Nemt, trygt og til at betale. 4 faste årlige hjemmebehandlinger (hver 3. måned) til en fast lav månedlig betaling.
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/ydelser/abonnement"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#A67C3D] hover:bg-[#91692E] text-white font-bold py-2.5 rounded-full text-xs shadow transition-colors"
                >
                  <span>Se abonnementsfordele</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Service 3: Laserbehandling */}
            <div className="bg-[#FAF7F2] rounded-3xl overflow-hidden border border-[#E7E0D2] shadow-xs hover:border-[#A67C3D] transition-all flex flex-col justify-between group">
              <div>
                <div className="aspect-16/10 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/d4428667-cde9-4c31-9224-88278506a01a/forside-seniorer.webp"
                    alt="Glade kunder og tryg laserbehandling i hjemmet"
                    className="w-full h-full object-cover object-center group-hover:scale-103 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3 bg-stone-900 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow">
                    Laserterapi
                  </div>
                </div>
                <div className="p-6 space-y-2">
                  <h3 className="text-base font-bold text-stone-900 group-hover:text-[#A67C3D] transition-colors">
                    Laserbehandling i Hjemmet
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Smertefri og skånsom laserterapi mod neglesvamp, hælsporer og ledsmerter. Vi medbringer vores kompakte speciallaser.
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/ydelser/laserbehandling"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-stone-50 text-stone-900 font-bold py-2.5 rounded-full text-xs border border-[#E7E0D2] transition-colors"
                >
                  <span>Læs om laserbehandling</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Service 4: Naturlige Fodcremer */}
            <div className="bg-[#FAF7F2] rounded-3xl overflow-hidden border border-[#E7E0D2] shadow-xs hover:border-[#A67C3D] transition-all flex flex-col justify-between group">
              <div>
                <div className="aspect-16/10 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/d4428667-cde9-4c31-9224-88278506a01a/fodtid-natural-creams-bottles.png"
                    alt="100% Naturlige Urtecremer til fødder"
                    className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3 bg-stone-900 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow">
                    Fra 89 kr.
                  </div>
                </div>
                <div className="p-6 space-y-2">
                  <h3 className="text-base font-bold text-stone-900 group-hover:text-[#A67C3D] transition-colors">
                    Naturlige Fodcremer
                  </h3>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                    Udelukkende fremstillet af naturlige urter og plejende olier mod tør hud, hælrevner og svedige fødder.
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/produkter"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-stone-50 text-stone-900 font-bold py-2.5 rounded-full text-xs border border-[#E7E0D2] transition-colors"
                >
                  <span>Se udvalg & bestil</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* REVIEWS & REAL SENIOR VOICES SLIDER */}
      <section className="py-16 sm:py-24 bg-[#FAF7F2] border-b border-[#E7E0D2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-2">
            <span className="text-xs uppercase font-bold tracking-widest text-[#A67C3D]">
              Kundetilfredshed
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Hvad siger vores kunder om hjemmebesøget?
            </h2>
            <p className="font-accent text-2xl text-[#A67C3D]">
              ægte tilbagemeldinger fra glade fødder på Sjælland
            </p>
          </div>

          <ReviewSlider reviews={reviews} />

        </div>
      </section>

      {/* SERVICE COVERAGE AREA SECTION (GEOGRAPHIC FOCUS) */}
      <section className="py-16 sm:py-20 bg-white border-b border-[#E7E0D2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="bg-[#FAF7F2] rounded-3xl p-8 sm:p-12 border border-[#E7E0D2] grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            <div className="lg:col-span-7 space-y-4">
              <span className="text-xs uppercase font-bold tracking-widest text-[#66461D] bg-[#F7EEDB] px-3.5 py-1 rounded-full border border-[#A67C3D]/30 inline-block">
                Kørselsområde
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Vi kører ud på hele Sjælland – minus øerne
              </h2>
              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                Vores servicebil kører fast ud til private hjem, ældreboliger og plejehjem. Fodtid dækker hele Sjælland (bortset fra øerne) med professionel fodbehandling direkte i dine egne trygge rammer.
              </p>

              <div className="space-y-2.5 pt-2 text-xs sm:text-sm font-medium text-stone-700">
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-[#A67C3D]" aria-hidden="true" />
                  <span>Udkørende til private hjem, ældreboliger og plejehjem</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-[#A67C3D]" aria-hidden="true" />
                  <span>Dækker hele Sjælland (undtagen ikke-brofaste øer)</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-[#A67C3D]" aria-hidden="true" />
                  <span>Alt professionelt udstyr medbringes i bilen</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col items-center lg:items-end justify-center">
              <div className="bg-white rounded-3xl overflow-hidden border border-[#E7E0D2] shadow-sm w-full max-w-sm group">
                <div className="aspect-4/3 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/d4428667-cde9-4c31-9224-88278506a01a/forside-foedder-familie.webp"
                    alt="Sunde og velplejede fødder – professionel fodpleje i hjemmet"
                    className="w-full h-full object-cover object-bottom group-hover:scale-103 transition-transform duration-300"
                  />
                </div>
                <div className="p-5 text-center space-y-3">
                  <h3 className="font-bold text-stone-900 text-base">Bor du på Sjælland?</h3>
                  <p className="text-xs text-stone-500">Ring eller book online – vi finder en tid der passer dig.</p>
                  <Link
                    to="/kontakt"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#A67C3D] hover:bg-[#91692E] text-white font-bold py-3 rounded-full text-xs uppercase tracking-wider shadow-sm transition-all"
                  >
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    <span>Bestil hjemmebesøg</span>
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CONTACT & BOOKING SECTION */}
      <section className="py-16 sm:py-24 bg-[#FAF7F2]" id="kontakt-sektion">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Contact Info Column */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs uppercase font-bold tracking-widest text-[#A67C3D]">
                  Bestil Hjemmebehandling
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mt-1">
                  Kontakt os & Book tid
                </h2>
                <p className="font-accent text-2xl text-[#A67C3D] mt-0.5">
                  vi glæder os til at besøge dig og give dine fødder nyt liv
                </p>
              </div>

              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                Udfyld formularen for at forespørge på en tid, eller ring direkte til os på telefonen. Vi svarer hurtigt og bekræfter dit hjemmebesøg.
              </p>

              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-[#E7E0D2]">
                  <div className="w-12 h-12 rounded-full bg-[#FAF7F2] border border-[#A67C3D]/30 flex items-center justify-center text-[#A67C3D] shrink-0">
                    <i className="fa-solid fa-phone text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="block text-[11px] uppercase font-bold text-stone-500">Telefonisk tidsbestilling</span>
                    <a href={`tel:${phone.replace(/\s+/g, '')}`} className="text-lg font-bold text-stone-900 hover:text-[#A67C3D] transition-colors">
                      {phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-[#E7E0D2]">
                  <div className="w-12 h-12 rounded-full bg-[#FAF7F2] border border-[#A67C3D]/30 flex items-center justify-center text-[#A67C3D] shrink-0">
                    <i className="fa-regular fa-clock text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="block text-[11px] uppercase font-bold text-stone-500">Kørsels- og åbningstider</span>
                    <span className="text-sm font-bold text-stone-900">{hours}</span>
                  </div>
                </div>

                <div className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-[#E7E0D2]">
                  <div className="w-12 h-12 rounded-full bg-[#FAF7F2] border border-[#A67C3D]/30 flex items-center justify-center text-[#A67C3D] shrink-0">
                    <i className="fa-solid fa-van-shuttle text-lg" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="block text-[11px] uppercase font-bold text-stone-500">Område</span>
                    <span className="text-sm font-bold text-stone-900">Udkørende på hele Sjælland (minus øerne)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Webform Column */}
            <div className="lg:col-span-7 bg-white rounded-3xl p-6 sm:p-10 border border-[#E7E0D2] shadow-sm">
              <ContactForm />
            </div>

          </div>

        </div>
      </section>
    </>
  );
}
